import React, { useRef, useState } from "react";
import { Modal, Container, Row, Col, Button } from "react-bootstrap";
import { AiOutlineDownload, AiOutlineMail, AiOutlinePhone, AiOutlineEnvironment, AiOutlineClose } from "react-icons/ai";
import { FaLinkedinIn, FaGithub } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./ResumeModal.css";

function ResumeModal({ show, onHide }) {
  const resumeRef = useRef();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadPDF = async () => {
    if (isDownloading) return; 
    
    setIsDownloading(true);
    
    try {
      const element = resumeRef.current;
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }

      pdf.save('Yamini_Chaudhary_Resume.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const personalInfo = {
    name: "Yamini Chaudhary",
    title: "Full-Stack Developer & Mobile App Creator",
    email: "yaminichaudhary9104@gmail.com",
    phone: "+91 6395426205",
    location: "India",
    linkedin: "linkedin.com/in/yamini-chaudhary",
    github: "github.com/yamini-chaudhary"
  };

  const experience = [
    {
      title: "Full Stack Developer",
      company: "Quark",
      duration: "January 2026 – Present",
      description: [
        "Developing a dating and matchmaking app for book lovers, connecting people through shared reading interests",
        "Building book-based matching algorithms, community forums, and in-app messaging with React Native",
        "Implementing backend services with Express.js and MongoDB for real-time user interactions and recommendations",
        "Managing AWS infrastructure and deployments, with Render used for additional service hosting"
      ]
    },
    {
      title: "Product Engineer",
      company: "GazeSEO",
      duration: "November 2025 – July 2026",
      description: [
        "Built an SEO automation platform that streamlines content creation, publishing, and scheduling for businesses and marketers",
        "Developed keyword research tools, performance analytics dashboards, and multi-platform publishing workflows",
        "Worked with Next.js, Express.js, and PostgreSQL, with Grafana and Langfuse for monitoring and observability",
        "Owned AWS deployment and infrastructure, managing environments and release rollouts for the platform"
      ]
    },
    {
      title: "Full Stack Engineer",
      company: "Knowledge Keeper",
      duration: "July 2024 – October 2025",
      description: [
        "Developing responsive web applications using React.js for AI-powered knowledge management platform",
        "Building user interfaces for real-time knowledge capture from Slack or dashboard",
        "Worked on WuduAi healthcare platform frontend (Next.js) and backend (Node.js, MongoDB)",
        "Collaborating with backend team to integrate APIs and optimize application performance"
      ]
    },
    {
      title: "Full-stack Developer",
      company: "Ovolo",
      duration: "May 2023 – May 2025",
      description: [
        "Developed cross-platform mobile applications using Flutter for Android and iOS",
        "Built Ekinch EdTech platform with live classes, job marketplace, and recruitment features",
        "Integrated Firebase for authentication, real-time database, and cloud storage",
      ]
    },
    {
      title: "Flutter Developer",
      company: "iByte Infomatics Inc.",
      duration: "June 2022 – May 2023",
      description: [
        "Built and deployed mobile applications using Flutter for Android and iOS",
        "Implemented responsive UI/UX designs and integrated RESTful APIs",
        "Added Firebase authentication, push notifications, and cloud storage",
        "Worked with backend team to ensure smooth data flow and app performance"
      ]
    },
    {
      title: "Intern",
      company: "AnyTechTrial.Com",
      duration: "July 2021 – September 2021",
      description: [
        "Assisted in developing and testing web modules using HTML, CSS, and JavaScript",
        "Gained hands-on experience with front-end and back-end technologies",
        "Participated in code reviews and debugging sessions"
      ]
    }
  ];
  
  const education = [
    {
      degree: "Bachelor of Technology in Computer Science",
      institution: "College of Engineering Roorkee",
      duration: "2018 – 2022",
      grade: "CGPA: 8.5/10"
    },
    {
      degree: "Senior Secondary (12th) - CBSE",
      institution: "DS Public School",
      duration: "2017 – 2018",
      grade: "Percentage: 85%"
    },
    {
      degree: "Secondary (10th) - CBSE",
      institution: "DS Public School",
      duration: "2015 – 2016",
      grade: "CGPA: 9.8/10"
    }
  ];
  
  const skills = {
    frontend: ["React.js", "Next.js", "JavaScript", "HTML5", "CSS3", "Bootstrap"],
    backend: ["Node.js", "Express.js", "NestJS", "RESTful APIs"],
    database: ["MongoDB", "Firebase"],
    mobile: ["Flutter", "React Native"],
    tools: ["Git", "Postman", "VS Code", "Swagger"],
    devops: ["AWS", "Google Cloud", "Render", "Deployment & Release Management"]
  };
  
  const projects = [
    {
      name: "Quark",
      tech: "React Native, Express.js, MongoDB, AWS, Render",
      description:
        "Dating and matchmaking app for book lovers with book-based matching, community forums, and cross-platform support. Managing AWS and Render deployments for backend services."
    },
    {
      name: "GazeSEO",
      tech: "Next.js, Express.js, PostgreSQL, AWS",
      description:
        "SEO automation platform for content creation, publishing, and scheduling with keyword research and analytics. Owned AWS deployment and infrastructure for the platform."
    },
    {
      name: "Ekinch",
      tech: "Flutter",
      description:
        "Comprehensive EdTech and career development platform with live interactive classes, job marketplace, and company recruitment tools for Android and iOS."
    },
    {
      name: "Pborbit",
      tech: "Flutter, NestJS, MongoDB, Firebase",
      description:
        "Pickleball community management platform with event scheduling, court reservations, real-time leaderboards, and in-app messaging for players and organizers."
    },
    {
      name: "Knowledge Keeper",
      tech: "React.js",
      description:
        "AI-powered knowledge management platform that captures real-time insights from Slack, Teams, and email to maintain dynamic, structured documentation."
    },
    {
      name: "WuduAi",
      tech: "Next.js",
      description:
        "Healthcare compliance platform using computer vision AI to monitor hand hygiene, PPE usage, and cleaning protocols in hospitals via CCTV analysis."
    },
    {
      name: "TaskPath",
      tech: "Next.js, Firebase",
      description:
        "Modern task management application with real-time synchronization, smart deadline reminders, and secure authentication across all devices."
    },
    {
      name: "Skoot",
      tech: "Flutter",
      description:
        "Scooter rental platform for Thailand with instant booking, secure ID verification, digital contracts, and Google Maps integration for location tracking."
    },
    {
      name: "Speke",
      tech: "Flutter",
      description:
        "Telemedicine platform connecting patients with doctors for video consultations, appointment scheduling, digital prescriptions, and health record management."
    }
  ];
  

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="resume-modal"
      backdrop="static"
    >
      <Modal.Header className="resume-modal-header">
        <div className="modal-header-content">
          <h4 className="modal-title">Resume</h4>
          <div className="header-actions">
            <Button 
              variant="outline-primary"
              size="sm"
              onClick={downloadPDF}
              disabled={isDownloading}
              className={`download-btn-modal ${isDownloading ? 'downloading' : ''}`}
            >
              {isDownloading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <AiOutlineDownload className="me-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button 
              variant="outline-secondary"
              size="sm"
              onClick={onHide}
              className="close-btn-modal"
            >
              <AiOutlineClose />
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="resume-modal-body">
        <div ref={resumeRef} className="resume-content-modal">
          {/* Header Section */}
          <div className="resume-header-modal">
            <div className="header-content-modal">
              <h1 className="name-modal">{personalInfo.name}</h1>
              <h2 className="title-modal">{personalInfo.title}</h2>
              
              <div className="contact-info-modal">
                <div className="contact-item-modal">
                  <AiOutlineMail className="contact-icon-modal" />
                  <span>{personalInfo.email}</span>
                </div>
                <div className="contact-item-modal">
                  <AiOutlinePhone className="contact-icon-modal" />
                  <span>{personalInfo.phone}</span>
                </div>
                <div className="contact-item-modal">
                  <AiOutlineEnvironment className="contact-icon-modal" />
                  <span>{personalInfo.location}</span>
                </div>
                <div className="contact-item-modal">
                  <FaLinkedinIn className="contact-icon-modal" />
                  <span>{personalInfo.linkedin}</span>
                </div>
                <div className="contact-item-modal">
                  <FaGithub className="contact-icon-modal" />
                  <span>{personalInfo.github}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Summary */}
          <div className="resume-section-modal">
            <h3 className="section-title-modal">Professional Summary</h3>
            <p className="summary-text-modal">
             Full-Stack Developer with over 4 years of experience in building fast, scalable web and mobile applications. Skilled in the MERN stack and Flutter, with a strong focus on creating smooth, user-friendly products. Experienced in delivering high-quality projects and leading small development teams to success.  </p>
          </div>

          {/* Experience Section */}
          <div className="resume-section-modal">
            <h3 className="section-title-modal">Professional Experience</h3>
            {experience.map((exp, index) => (
              <div key={index} className="experience-item-modal">
                <div className="exp-header-modal">
                  <div>
                    <h4 className="exp-title-modal">{exp.title}</h4>
                    <h5 className="exp-company-modal">{exp.company}</h5>
                  </div>
                  <span className="exp-duration-modal">{exp.duration}</span>
                </div>
                <ul className="exp-description-modal">
                  {exp.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Skills Section */}
          <div className="resume-section-modal">
            <h3 className="section-title-modal">Technical Skills</h3>
            <div className="skills-grid-modal">
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Frontend</h4>
                <div className="skill-tags-modal">
                  {skills.frontend.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Backend</h4>
                <div className="skill-tags-modal">
                  {skills.backend.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Database</h4>
                <div className="skill-tags-modal">
                  {skills.database.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Mobile</h4>
                <div className="skill-tags-modal">
                  {skills.mobile.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Tools & Technologies</h4>
                <div className="skill-tags-modal">
                  {skills.tools.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
              <div className="skill-category-modal">
                <h4 className="skill-category-title-modal">Cloud & Deployment</h4>
                <div className="skill-tags-modal">
                  {skills.devops.map((skill, index) => (
                    <span key={index} className="skill-tag-modal">{skill}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Projects Section */}
          <div className="resume-section-modal">
            <h3 className="section-title-modal">Key Projects</h3>
            {projects.map((project, index) => (
              <div key={index} className="project-item-modal">
                <div className="project-header-modal">
                  <h4 className="project-name-modal">{project.name}</h4>
                  <span className="project-tech-modal">{project.tech}</span>
                </div>
                <p className="project-description-modal">{project.description}</p>
              </div>
            ))}
          </div>

          {/* Education Section */}
          <div className="resume-section-modal">
            <h3 className="section-title-modal">Education</h3>
            {education.map((edu, index) => (
              <div key={index} className="education-item-modal">
                <div className="edu-header-modal">
                  <div>
                    <h4 className="edu-degree-modal">{edu.degree}</h4>
                    <h5 className="edu-institution-modal">{edu.institution}</h5>
                  </div>
                  <div className="edu-details-modal">
                    <span className="edu-duration-modal">{edu.duration}</span>
                    <span className="edu-grade-modal">{edu.grade}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ResumeModal;
